import React, { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Button,
  Avatar,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'

type Provider = {
  id: string
  type: string
  name: string
  email: string
  contact: string
  location: string
  specialty: string
}

const SAMPLE_PROVIDERS: Provider[] = [
  {
    id: 'p1',
    type: 'Doctor',
    name: 'Dr. Sarah Johnson',
    email: 'Sarah.Johnson@sample.com',
    contact: '987-654-3210',
    location: '200 1st St SW, Rochester',
    specialty: 'Cardiology',
  },
  {
    id: 'p2',
    type: 'Doctor',
    name: 'Dr. Kevin Morgan',
    email: 'Kevin.morgan@sample.com',
    contact: '765-514-4000',
    location: '500 Pasteur Drive',
    specialty: 'Neurology',
  },
  {
    id: 'p3',
    type: 'Device Seller',
    name: 'Medtronic',
    email: 'relations@medtronic.com',
    contact: '650-723-4000',
    location: 'Minneapolis, MN',
    specialty: 'Insulin pumps and sugar chips',
  },
  {
    id: 'p4',
    type: 'Lab',
    name: 'Quest Diagnostics',
    email: 'Contact@QuestDiagnostics.com',
    contact: '866-697-8378',
    location: '500 Plaza Drive',
    specialty: 'Blood, urine, COVID-19, STD',
  },
]

function TabPanel({ children, index, currentTab }: { children: React.ReactNode; index: number; currentTab: number }) {
  if (currentTab !== index) return null
  return <Box sx={{ mt: 2 }}>{children}</Box>
}

export default function Providers(): React.ReactElement {
  const [tab, setTab] = useState(0)
  const [modalOpen, setModalOpen] = useState(false)

  function handleTabChange(_: React.SyntheticEvent, value: number) {
    setTab(value)
  }

  const handleSendRequest = () => {
    setModalOpen(true)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
  }

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5">Providers</Typography>
            <Typography variant="body2" color="text.secondary">Manage provider connections</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" size="small">Export</Button>
            <Button variant="contained" size="small">+ Add Patient</Button>
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Tabs value={tab} onChange={handleTabChange} aria-label="providers-tabs">
            <Tab label="Providers Network" />
            <Tab label="Connected Providers" />
          </Tabs>

          <TabPanel index={0} currentTab={tab}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>List</Typography>

              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"><Checkbox /></TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Specialty/Services</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {SAMPLE_PROVIDERS.map(p => (
                    <TableRow key={p.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox />
                      </TableCell>
                      <TableCell>{p.type}</TableCell>
                      <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28 }}>{p.name.split(' ').map(n => n[0]).slice(0,2).join('')}</Avatar>
                        {p.name}
                      </TableCell>
                      <TableCell>{p.email}</TableCell>
                      <TableCell>{p.contact}</TableCell>
                      <TableCell>{p.location}</TableCell>
                      <TableCell>{p.specialty}</TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined" onClick={handleSendRequest}>Send Request</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </TabPanel>

          <TabPanel index={1} currentTab={tab}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="body1" color="text.secondary">Connected Providers content goes here.</Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                This view will show providers already connected to your organization.
              </Typography>
            </Paper>
          </TabPanel>
        </Box>
      </Box>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary',
            }}
          >
            <CloseIcon />
          </IconButton>
          <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" component="div" sx={{ mb: 1 }}>
            Success
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your request has been successfully sent.
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  )
}
